import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, ChevronRight } from 'lucide-react';
import BlogLayout from '../../components/BlogLayout';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Ποια είναι τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Τα πιο συχνά λάθη είναι η βιαστική απόφαση, το να ακολουθείς τυφλά τις τάσεις, η ακριβής αντιγραφή τατουάζ άλλων, η αμέλεια του σημείου και του μεγέθους, η έλλειψη μακροπρόθεσμης σκέψης και το να βασίζεσαι μόνο στην τιμή." } },
    { "@type": "Question", name: "Πόσο χρόνο πρέπει να σκεφτώ πριν κάνω τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Δεν υπάρχει σταθερός κανόνας, αλλά καλό είναι να αφήσεις την ιδέα να ωριμάσει για αρκετές εβδομάδες ή και μήνες. Αν εξακολουθείς να θες το ίδιο σχέδιο με την ίδια ένταση, αυτό είναι καλό σημάδι ότι η απόφαση θα κρατήσει." } },
    { "@type": "Question", name: "Μπορώ να αντιγράψω ακριβώς ένα τατουάζ που είδα;", acceptedAnswer: { "@type": "Answer", text: "Καλύτερα όχι. Χρησιμοποίησε τις αναφορές ως έμπνευση και άφησε τον καλλιτέχνη να δημιουργήσει κάτι μοναδικό για σένα. Έτσι καταλήγεις με ένα αυθεντικό κομμάτι, σχεδιασμένο για το δικό σου σώμα και στυλ." } },
    { "@type": "Question", name: "Πόσο σημαντικό είναι το μέγεθος του τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Πολύ. Ένα λεπτομερές σχέδιο σε πολύ μικρό μέγεθος χάνει τις λεπτομέρειες και μπορεί να γίνει δυσανάγνωστο με τον χρόνο. Κάθε σχέδιο χρειάζεται το σωστό μέγεθος για να αναδειχτεί και να αντέξει." } },
    { "@type": "Question", name: "Πρέπει να ακολουθώ τις τάσεις στα τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Οι τάσεις περνάνε, ενώ το τατουάζ μένει. Αν αγαπάς πραγματικά ένα δημοφιλές σχέδιο, η δημοτικότητά του δεν είναι πρόβλημα. Το λάθος είναι να διαλέξεις κάτι μόνο επειδή είναι στη μόδα, χωρίς προσωπική αξία." } },
    { "@type": "Question", name: "Γιατί πρέπει να εμπιστευτώ τις συμβουλές του καλλιτέχνη;", acceptedAnswer: { "@type": "Answer", text: "Ένας έμπειρος καλλιτέχνης ξέρει πώς συμπεριφέρεται το μελάνι, πώς ξεθωριάζουν τα σχέδια, ποια μεγέθη και τοποθετήσεις λειτουργούν και ποιες λεπτομέρειες θα χαθούν. Οι συμβουλές του στοχεύουν στο καλύτερο δυνατό αποτέλεσμα για σένα." } },
    { "@type": "Question", name: "Πώς διαλέγω το σωστό στυλ τατουάζ;", acceptedAnswer: { "@type": "Answer", text: "Σκέψου ποιο στυλ ταιριάζει στο σχέδιό σου και στην προσωπικότητά σου, από realism και fine line μέχρι traditional και blackwork. Μετά βρες έναν καλλιτέχνη που εξειδικεύεται σε αυτό το στυλ για το καλύτερο αποτέλεσμα." } },
  ],
};

export default function TattooDesignMistakesAthens() {
  useEffect(() => {
    const inject = (id: string, s: object) => {
      let tag = document.getElementById(id);
      if (!tag) { tag = document.createElement('script'); tag.id = id; (tag as HTMLScriptElement).type = 'application/ld+json'; document.head.appendChild(tag); }
      tag.textContent = JSON.stringify(s);
    };
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ",
      "description": "Τα 10 πιο συχνά λάθη που κάνουν οι άνθρωποι όταν διαλέγουν σχέδιο τατουάζ και πώς να τα αποφύγεις ώστε να καταλήξεις με ένα κομμάτι που θα αγαπάς για πάντα.",
      "image": "https://210tattoo.gr/og-image.png",
      "datePublished": "2026-06-01",
      "dateModified": "2026-06-01",
      "author": { "@type": "Organization", "name": "210 Tattoo Athens", "url": "https://210tattoo.gr" },
      "publisher": { "@id": "https://210tattoo.gr/#tattooparlor" },
      "url": "https://210tattoo.gr/blog/tattoo-design-mistakes-athens",
      "inLanguage": "el"
    };
    inject('schema-tattoo-design-mistakes-faq', faqSchema);
    inject('schema-tattoo-design-mistakes-article', articleSchema);
    return () => {
      document.getElementById('schema-tattoo-design-mistakes-faq')?.remove();
      document.getElementById('schema-tattoo-design-mistakes-article')?.remove();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ | 210 Tattoo Athens</title>
        <meta name="description" content="Τα 10 πιο συχνά λάθη που κάνουν οι άνθρωποι όταν διαλέγουν σχέδιο τατουάζ και πώς να τα αποφύγεις ώστε να καταλήξεις με ένα κομμάτι που θα αγαπάς για πάντα." />
        <link rel="canonical" href="https://210tattoo.gr/blog/tattoo-design-mistakes-athens" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog/tattoo-design-mistakes-athens" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog/tattoo-design-mistakes-athens" />
        <meta property="og:title" content="Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ | 210 Tattoo Athens" />
        <meta property="og:description" content="Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ και πώς να τα αποφύγεις." />
        <meta property="og:url" content="https://210tattoo.gr/blog/tattoo-design-mistakes-athens" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage('tattoo-design-mistakes-athens')} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Πώς να αποφύγεις τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ." />
        <meta name="twitter:image" content={ogImage('tattoo-design-mistakes-athens')} />
      </Helmet>

      <BlogLayout>
        {/* Hero */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Επιλογή Σχεδίου Τατουάζ</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Τα πιο συχνά λάθη</span>
              <br />
              <span className="text-white">στην επιλογή σχεδίου τατουάζ</span>
            </h1>
            <p className="text-gray-500 text-xs mb-4 tracking-widest uppercase">1 Ιουνίου 2026</p>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl">
              Το τατουάζ είναι μόνιμο, γι' αυτό η επιλογή σχεδίου είναι μία από τις πιο σημαντικές αποφάσεις. Δες τα πιο συχνά λάθη και πώς να τα αποφύγεις.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Κλείσε Ραντεβού
            </a>
          </div>
        </section>

        {/* Mistakes list */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {[
                { num: '01', title: 'Η βιαστική απόφαση', desc: 'Πολλοί αποφασίζουν μέσα σε λίγες μέρες, παρασυρμένοι από στιγμιαία έμπνευση. Άφησε την ιδέα να ωριμάσει για εβδομάδες ή μήνες πριν κλείσεις ραντεβού.' },
                { num: '02', title: 'Να ακολουθείς τυφλά τις τάσεις', desc: 'Οι τάσεις περνάνε, το τατουάζ μένει. Διάλεξε κάτι που αγαπάς πραγματικά, όχι απλώς επειδή είναι στη μόδα.' },
                { num: '03', title: 'Να αντιγράφεις ακριβώς τατουάζ άλλου', desc: 'Χρησιμοποίησε αναφορές ως έμπνευση, όχι για ακριβή αντιγραφή. Άφησε τον καλλιτέχνη να δημιουργήσει κάτι μοναδικό για σένα.' },
                { num: '04', title: 'Να αγνοείς το σημείο του σώματος', desc: 'Το σχήμα, το μέγεθος και η κίνηση του δέρματος επηρεάζουν τεράστια το πώς θα φαίνεται το σχέδιο.' },
                { num: '05', title: 'Να επιλέγεις λάθος μέγεθος', desc: 'Ένα πολύ μικρό μέγεθος για λεπτομερές σχέδιο κάνει τις γραμμές να μπερδεύονται με τον χρόνο.' },
                { num: '06', title: 'Να μην σκέφτεσαι το μακροπρόθεσμο', desc: 'Σκέψου αν το σχέδιο θα έχει νόημα και αξία και σε δέκα ή είκοσι χρόνια.' },
                { num: '07', title: 'Να μην εμπιστεύεσαι τον καλλιτέχνη', desc: 'Η εμπειρία του καλλιτέχνη ξέρει τι λειτουργεί τεχνικά. Βρες ισορροπία ανάμεσα στο όραμά σου και τη συμβουλή του.' },
                { num: '08', title: 'Να διαλέγεις σχέδιο σε λάθος ψυχολογική κατάσταση', desc: 'Τα τατουάζ που γίνονται σε στιγμές έντονου συναισθήματος συχνά μετανιώνονται περισσότερο. Αποφάσισε με καθαρό μυαλό.' },
                { num: '09', title: 'Να αμελείς το στυλ', desc: 'Κάθε στυλ έχει διαφορετικές απαιτήσεις. Βρες καλλιτέχνη που εξειδικεύεται σε αυτό που θέλεις.' },
                { num: '10', title: 'Να βασίζεσαι μόνο στην τιμή', desc: 'Το τατουάζ είναι μόνιμο. Η ποιότητα αξίζει την επένδυση, η φθηνότερη επιλογή σπάνια είναι η καλύτερη.' },
              ].map((item) => (
                <div key={item.num} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-pink-600/30 shrink-0 w-12 text-center leading-none pt-1">{item.num}</div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 flex-1">
                    <h2 className="text-white font-semibold text-lg mb-2">{item.title}</h2>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to choose right */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Πώς να επιλέξεις σωστά <span className="text-pink-500">το σχέδιό σου</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Δώσε χρόνο στην ιδέα σου να ωριμάσει',
                'Διάλεξε κάτι που αγαπάς πραγματικά',
                'Χρησιμοποίησε αναφορές ως έμπνευση, όχι αντιγραφή',
                'Σκέψου το σημείο του σώματος',
                'Διάλεξε μέγεθος που επιτρέπει στο σχέδιο να αναδειχτεί',
                'Σκέψου τη μακροπρόθεσμη αξία',
                'Εμπιστεύσου τις συμβουλές του καλλιτέχνη',
                'Βρες καλλιτέχνη εξειδικευμένο στο στυλ σου',
              ].map((text, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-5 border border-pink-600/10 flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0 mt-2" />
                  <p className="text-gray-300 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Role of artist */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Ο ρόλος του καλλιτέχνη <span className="text-pink-500">στη σωστή επιλογή</span></h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Ένας καλός tattoo artist δεν εκτελεί απλώς αυτό που του ζητάς. Σε ακούει, σε καθοδηγεί, σου επισημαίνει πιθανά προβλήματα και σε βοηθάει να πάρεις τη σωστή απόφαση. Από το μέγεθος και την τοποθέτηση μέχρι το στυλ και τη διαχρονικότητα, αυτή η συνεργασία είναι το κλειδί για ένα τατουάζ που θα παραμείνει όμορφο για χρόνια.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-8">Συχνές <span className="text-pink-500">Ερωτήσεις</span></h2>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((item, i) => (
                <div key={i} className="bg-gray-900 rounded-xl p-6 border border-pink-600/10">
                  <h3 className="text-white font-semibold mb-2">{item.name}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">Θέλεις να πάρεις <span className="text-pink-500">τη σωστή απόφαση;</span></h2>
            <p className="text-gray-300 text-lg mb-8">
              Η ομάδα του 210 Tattoo Athens θα σε συμβουλέψει για το μέγεθος, την τοποθέτηση και το στυλ, ώστε να καταλήξεις με ένα κομμάτι που θα αγαπάς για πάντα.
            </p>
            <a href="https://ig.me/m/210tattoo" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105">
              <Instagram size={20} />
              Στείλε DM στο 210 Tattoo Athens
            </a>
          </div>
        </section>

        {/* Related articles */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Σχετικά <span className="text-pink-500">Άρθρα</span></h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/first-tattoo-design-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Ιδέες και συμβουλές για το πρώτο σου tattoo.</p>
              </Link>
              <Link to="/blog/tattoo-style-guide-athens" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Blackwork, Fine Line ή Realism; Ποιο Στυλ να Διαλέξεις</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς να βρεις το στυλ που ταιριάζει στην αισθητική σου.</p>
              </Link>
              <Link to="/blog/pio-syxna-lathi-epilogi-sxediou-tatouaz-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Λάθη στην Επιλογή Σχεδίου Τατουάζ</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Γιατί συμβαίνουν τα πιο συχνά λάθη και πώς να τα αποφύγεις.</p>
              </Link>
              <Link to="/blog/tatouaz-aresei-se-10-xronia-athina" className="bg-gray-900 rounded-xl p-6 border border-pink-600/10 hover:border-pink-600/30 transition-colors duration-200 group">
                <p className="text-pink-500 text-xs uppercase tracking-widest mb-2">Άρθρο</p>
                <h3 className="text-white font-semibold group-hover:text-pink-400 transition-colors">Θα σου Αρέσει το Τατουάζ σε 10 Χρόνια;</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">Πώς αξιολογείς ένα σχέδιο για τη διαχρονικότητά του.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Υπηρεσίες <span className="text-pink-500">/ Services</span></h2>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/fine-line-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Fine Line Tattoo Athens</a>
              <a href="/realistic-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Realistic Tattoo Athens</a>
              <a href="/cover-up-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Cover Up Tattoo Athens</a>
              <a href="/black-gray-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Black &amp; Gray Tattoo Athens</a>
              <a href="/custom-tattoo-athens" className="border border-pink-600/20 hover:border-pink-500 text-gray-400 hover:text-white px-5 py-2 rounded-lg text-sm transition-colors duration-200">Custom Tattoo Athens</a>
            </div>
          </div>
        </section>
      </BlogLayout>
    </>
  );
}
